import { forwardRef, type ElementType } from 'react';
import { Card } from '../Card';
import type { CardProps } from '../Card';
import { ListItem } from '../ListItem';
import type { ListItemProps } from '../ListItem';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Props for the List component.
 *
 * `className` is intentionally omitted — List is a sealed design system
 * component. Its chrome (Card border, radius, background, dividers) is fixed
 * by design. Use named props for visual variants.
 */
export interface ListProps {
  /**
   * Array of items to render. Each item maps directly to `ListItemProps`.
   * The `as` prop on each item defaults to `"li"` and is consistent with
   * the `as="ul"` list root.
   */
  items: ListItemProps[];
  /**
   * Shadow depth forwarded to the underlying `Card`.
   * Omit for a flat card with no drop shadow.
   *
   * @see CardShadow
   */
  shadow?: CardProps['shadow'];
  /**
   * The HTML element to render as the list root inside the Card.
   * Defaults to `"ul"`.
   *
   * @default "ul"
   */
  as?: ElementType;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Primer List component.
 *
 * A sealed card-wrapped list. Renders a `Card` containing a `<ul>` of
 * `ListItem` rows separated by `divide-y` dividers. Each item supports an
 * optional left icon, title, optional subtitle, and trailing icon.
 *
 * ### Sealed component
 * `className` is not exposed. The List's chrome (card border, radius,
 * dividers) is fixed. If a new visual state is needed, add a prop.
 *
 * ### Data-driven
 * Pass items as an array via the `items` prop, consistent with `Select`,
 * `MultiSelect`, and `RadioSelect`.
 *
 * @example
 * <List
 *   items={[
 *     { title: 'Data consent', icon: 'lock', trailingIcon: 'chevron-right', onClick: () => {} },
 *     { title: 'Data request', icon: 'send', trailingIcon: 'chevron-right', onClick: () => {} },
 *     { title: 'Delete account', icon: 'trash', trailingIcon: 'chevron-right', onClick: () => {} },
 *   ]}
 * />
 *
 * @example
 * // With shadow
 * <List shadow="first" items={[...]} />
 */
export const List = forwardRef<HTMLElement, ListProps>(function List(
  { items, shadow, as: Component = 'ul' },
  ref
) {
  return (
    <Card shadow={shadow}>
      <Component ref={ref} className="divide-y divide-default">
        {items.map((item) => (
          <ListItem key={item.title} {...item} />
        ))}
      </Component>
    </Card>
  );
});

export default List;
